import { useAddFileRecordToLibrary } from "@/api/fileRecords";
import { trackActionInvoked } from "@/object-actions/trackObjectAction";
import type {
  ObjectActionDestinationSubmenu,
  ResolvedObjectAction,
  TransferDestination,
} from "@/object-actions/types";
import type { FileRecord, Workspace } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { useWritableWorkspaceIds } from "./useWritableWorkspaces";

/**
 * Add to workspace as a nested pop — the same destination cascade as Move to /
 * Copy to. Every workspace the user can see is listed; the ones they can't
 * edit render disabled (the backend checks `can_edit` on the destination).
 *
 * Clicking the workspace row commits to the workspace root; drilling in picks
 * the folder. The file keeps its id and a reference stays behind (A3), which
 * is why the toast says "still available here".
 */
export function useAddToWorkspaceCommit(
  record: FileRecord | null,
  /** A block's live node box, when the surface can see the node — the
   *  promoted record remembers the size the user last saw. */
  displaySize?: { width: number; height: number },
): (destination: TransferDestination) => void {
  const addToLibrary = useAddFileRecordToLibrary();
  const queryClient = useQueryClient();

  return useCallback(
    (destination: TransferDestination) => {
      if (!record?.id) return;
      trackActionInvoked("add-to-workspace", {
        objectKind: "file-record",
        objectSubtype: record.class,
      });
      // mutateAsync: the menu that hosts this cascade unmounts on close, and
      // per-call mutate callbacks are dropped with it.
      void (async () => {
        try {
          const data = await addToLibrary.mutateAsync({
            record,
            workspaceId: destination.workspaceId,
            parentPath: destination.folderPath || undefined,
            displaySize,
          });
          // Name where the file landed, never the pick: a silently-ignored
          // parent_path lands at the root, so the root form names only the
          // workspace (from the picker's cached list) and never the folder.
          const landed = data.file_record.path;
          const parent = landed.includes("/")
            ? landed.slice(0, landed.lastIndexOf("/"))
            : "";
          const workspace = queryClient
            .getQueryData<Workspace[]>(["workspaces"])
            ?.find((ws) => ws.id === data.file_record.workspace_id);
          const workspaceName = workspace
            ? workspace.is_private
              ? "My Workspace"
              : workspace.name
            : "the workspace";
          toast.success(
            parent
              ? `Added to Files › ${parent}`
              : `Added to ${workspaceName} — still available here`,
          );
        } catch {
          toast.error("Failed to add to the workspace");
        }
      })();
    },
    [addToLibrary, queryClient, record, displaySize],
  );
}

/**
 * The cascade descriptor for one record — shared by the registry attach and
 * hand-written menus (the canvas drawer). Null when the record can't promote
 * (already a workspace file or a reference). `enabled` gates the workspace
 * fetch so closed menus fetch nothing.
 */
export function useAddToWorkspaceDestinationSubmenu(
  record: FileRecord | null,
  enabled = true,
  displaySize?: { width: number; height: number },
): ObjectActionDestinationSubmenu | null {
  const commit = useAddToWorkspaceCommit(record, displaySize);

  const eligible =
    !!record?.id && record.class !== "workspace" && record.kind !== "link";
  const active = enabled && eligible;

  const writableWorkspaceIds = useWritableWorkspaceIds(active);

  return useMemo(() => {
    if (!active) return null;
    return {
      kind: "workspace-destination",
      operation: "add-to-workspace",
      commit,
      writableWorkspaceIds,
    };
  }, [active, commit, writableWorkspaceIds]);
}

export function useAttachAddToWorkspaceSubmenu(
  actions: ResolvedObjectAction[],
  record: FileRecord | null,
  displaySize?: { width: number; height: number },
): ResolvedObjectAction[] {
  const hasAction = actions.some((a) => a.id === "add-to-workspace");
  const submenu = useAddToWorkspaceDestinationSubmenu(
    record,
    hasAction,
    displaySize,
  );

  return useMemo(() => {
    if (!submenu || !hasAction) return actions;
    return actions.map((action) =>
      action.id === "add-to-workspace" ? { ...action, submenu } : action,
    );
  }, [actions, hasAction, submenu]);
}

/**
 * Group promotion for the canvas multi-select toolbar: one cascade (the
 * descriptor is built from the first record), one commit looping every
 * promotable record, one summary toast. Ineligible selection members never reach here —
 * the caller filters to container-class plain files.
 */
export function useAttachBulkAddToWorkspaceSubmenu(
  actions: ResolvedObjectAction[],
  records: FileRecord[],
  resolveDisplaySize?: (
    record: FileRecord,
  ) => { width: number; height: number } | undefined,
): ResolvedObjectAction[] {
  const hasAction = actions.some((a) => a.id === "add-to-workspace");
  const addToLibrary = useAddFileRecordToLibrary();
  const base = useAddToWorkspaceDestinationSubmenu(
    records[0] ?? null,
    hasAction && records.length > 0,
  );

  const commit = useCallback(
    (destination: TransferDestination) => {
      trackActionInvoked("add-to-workspace", {
        objectKind: "multi-selection",
        selectionCount: records.length,
      });
      void (async () => {
        let failed = 0;
        for (const record of records) {
          try {
            await addToLibrary.mutateAsync({
              record,
              workspaceId: destination.workspaceId,
              parentPath: destination.folderPath || undefined,
              displaySize: resolveDisplaySize?.(record),
            });
          } catch {
            failed += 1;
          }
        }
        const succeeded = records.length - failed;
        if (failed === 0) {
          toast.success(
            succeeded === 1
              ? `Added to ${destination.label} — still available here`
              : `Added ${succeeded} to ${destination.label} — still available here`,
          );
        } else {
          toast.error(
            `Added ${succeeded} of ${records.length} to ${destination.label}.`,
          );
        }
      })();
    },
    [addToLibrary, records, resolveDisplaySize],
  );

  return useMemo(() => {
    if (!base || !hasAction) return actions;
    const submenu: ObjectActionDestinationSubmenu = { ...base, commit };
    return actions.map((action) =>
      action.id === "add-to-workspace" ? { ...action, submenu } : action,
    );
  }, [actions, base, commit, hasAction]);
}
